/**
 * 푸시 알림 클릭 → 앱 내 원본 화면(채팅·일정 등) 딥링크 보존
 *
 * 스플래시/부팅·SW postMessage 타이밍에 링크가 사라지지 않도록 sessionStorage에 보관한다.
 */
const PENDING_KEY = 'caion_pending_notify'
const MAX_AGE_MS = 5 * 60 * 1000

export type PendingNotify = {
  link: string
  data?: Record<string, string>
  at: number
}

const toAppPath = (raw?: string | null): string => {
  const s = String(raw || '').trim()
  if (!s || s === 'undefined' || s === 'null') return ''
  if (s.startsWith('http')) {
    try {
      const u = new URL(s)
      const path = `${u.pathname}${u.search}${u.hash}`
      if (!path || path.includes('undefined')) return ''
      return path
    } catch {
      return ''
    }
  }
  const path = s.startsWith('/') ? s : `/${s}`
  if (path.includes('undefined')) return ''
  return path
}

/** 앱 내부로 안전한 딥링크인지 */
export const isSafeNotifyPath = (raw?: string | null): boolean => {
  const path = toAppPath(raw)
  if (!path || path === '/') return false
  if (path.startsWith('//')) return false
  // 로그인·가입·스플래시로 보내지 않음
  if (
    path === '/splash' ||
    path === '/entry' ||
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/reset-password') ||
    path.startsWith('/terms')
  ) {
    return false
  }
  return (
    path.startsWith('/caregiver') ||
    path.startsWith('/owner') ||
    path.startsWith('/sw') ||
    path.startsWith('/app/')
  )
}

export const stashPendingNotify = (
  rawLink?: string | null,
  data?: Record<string, any> | null
): string => {
  const link = toAppPath(rawLink)
  if (!isSafeNotifyPath(link)) return ''
  const payload: PendingNotify = {
    link,
    data: data
      ? Object.fromEntries(
          Object.entries(data)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => [k, String(v)])
        )
      : undefined,
    at: Date.now(),
  }
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
  return link
}

export const peekPendingNotify = (): PendingNotify | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingNotify
    if (!parsed?.link || !isSafeNotifyPath(parsed.link)) {
      sessionStorage.removeItem(PENDING_KEY)
      return null
    }
    if (Date.now() - Number(parsed.at || 0) > MAX_AGE_MS) {
      sessionStorage.removeItem(PENDING_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const consumePendingNotify = (): PendingNotify | null => {
  const pending = peekPendingNotify()
  try {
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* ignore */
  }
  return pending
}

/**
 * 콜드스타트: openWindow(딥링크) 로 열린 경우 현재 URL을 보관
 * (스플래시가 홈으로 덮어쓰기 전에 보존)
 */
export const captureBootDeepLink = (): void => {
  try {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (!isSafeNotifyPath(path)) return
    // 이미 더 구체적인 pending 이 있으면 유지
    const existing = peekPendingNotify()
    if (existing) return
    const data: Record<string, string> = {}
    const q = new URLSearchParams(window.location.search)
    if (q.get('openChat')) {
      data.type = 'center_chat'
      data.openChat = String(q.get('openChat'))
    }
    if (q.get('openSchedule')) {
      data.type = 'schedule_remind'
      data.openSchedule = '1'
      if (q.get('date')) data.date = String(q.get('date'))
    }
    if (path.includes('/chat')) data.type = 'center_chat'
    stashPendingNotify(path, data)
  } catch {
    /* ignore */
  }
}

/** 스플래시·가드에서 쓸 이동 대상 (없으면 null) */
export const pendingNotifyTarget = (): string | null => {
  const p = peekPendingNotify()
  return p?.link || null
}
